/* eslint-disable */
// API service for scheduler
import { convertTo12Hour, convertTo24Hour, calculateEndTime, API_CONFIG } from '@vtdr/shared';

// API_BASE_URL now points to our backend proxy instead of external clinic APIs
const API_BASE_URL = '/api/clinic'; // Our backend proxy endpoint

// Global clinic ID - will be set by the ClinicContext
let currentClinicId = null;

// Global token manager instance
let tokenManagerInstance = null;

export const setClinicId = (clinicId) => {
  currentClinicId = clinicId;
};

export const setTokenManager = (tokenManager) => {
  tokenManagerInstance = tokenManager;
};

/**
 * Get common headers for all API requests (now async to get JWT token)
 * @returns {Promise<Object>} Headers object with JWT token
 */
const getHeaders = async (includeContentType = false) => {
  const headers = {};

  // Get valid JWT token
  if (tokenManagerInstance) {
    try {
      const token = await tokenManagerInstance.getToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.error('Failed to get token for request:', error);
    }
  }

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

/**
 * Handle 401 responses with token refresh and retry
 * @param {string} url - Request URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
const fetchWithTokenRetry = async (url, options = {}, fetchFn = fetch) => {
  let response = await fetchFn(url, options);

  // Handle 401 with token refresh
  if (response.status === 401 && tokenManagerInstance) {
    try {
      // Clone the response before consuming its body
      const clonedResponse = response.clone();
      const errorData = await clonedResponse.json();
      
      if (errorData.code === 'TOKEN_EXPIRED') {
        console.log('🔄 Token expired, refreshing and retrying...');
        await tokenManagerInstance.refreshToken();
        
        // Retry with new token
        const newHeaders = await getHeaders(options.headers && options.headers['Content-Type'] === 'application/json');
        const retryResponse = await fetchFn(url, {
          ...options,
          headers: newHeaders,
        });
        
        return retryResponse;
      }
    } catch (error) {
      console.error('Error during token refresh retry:', error);
    }
  }

  return response;
};

/**
 * Fetch all available locations from all clinics
 * @returns {Promise<Array>} Array of location objects with clinic info
 */
export async function fetchLocations() {
  try {
    if (!currentClinicId) {
      throw new Error('Clinic ID not set. Call setClinicId() first.');
    }
    const url = `${API_BASE_URL}/${currentClinicId}/scheduler/clinics`;
    const headers = await getHeaders();
    
    const response = await fetchWithTokenRetry(url, {
      headers: headers,
    });
    
    if (!response.ok) throw new Error('Failed to fetch locations');
    const data = await response.json();

    // New response structure: { success, clinics, count }
    if (data.success && data.clinics) {
      // Return clinics directly as locations - each clinic becomes a location option
      const locations = data.clinics.map(clinic => ({
        id: clinic.id,
        name: clinic.shortName, // Main title: short name (no fallback)
        address: clinic.address, // Subtitle: address
        phone: clinic.phone, // Include phone number for emergency contact display
        emergencyMessage: clinic.emergencyMessage || '', // Clinic-configurable emergency contact text
        clinicId: clinic.id,
        clinicName: clinic.name,
        clinicAddress: clinic.address,
      }));
      return locations;
    }
    
    return data.data || data; // Fallback for other formats
  } catch (error) {
    console.error('Error fetching locations:', error);
    throw error;
  }
}

/**
 * Fetch vets/providers for a specific clinic
 * @param {string} clinicId - The ID of the clinic to fetch providers for
 * @returns {Promise<Array>} Array of provider objects
 */
export async function fetchProviders(clinicId) {
  try {
    if (!currentClinicId) {
      throw new Error('Clinic ID not set. Call setClinicId() first.');
    }
    if (!clinicId) {
      throw new Error('Clinic ID is required');
    }

    const url = `${API_BASE_URL}/${currentClinicId}/scheduler/users/${currentClinicId}/${clinicId}`;
    const headers = await getHeaders();
    
    const response = await fetchWithTokenRetry(url, {
      headers: headers,
    });
    
    if (!response.ok) throw new Error('Failed to fetch providers');
    const data = await response.json();
    
    // New response structure: { success, clinic, vets, count }
    if (data.success && data.vets) {
      return data.vets;
    }
    
    return data.data || data; // Fallback for other formats
  } catch (error) {
    console.error('Error fetching providers:', error);
    throw error;
  }
}

// Time conversion utilities are now imported from @vtdr/shared

/**
 * Fetch available time slots for a provider on a specific date
 * @param {string} clinicId - The ID of the clinic
 * @param {string} providerId - The ID of the selected provider (can be 'no_preference')
 * @param {string} date - The selected date in YYYY-MM-DD format
 * @param {Array} animals - Array of animal objects with name, reason, species, breed
 * @returns {Promise<Array|Object>} Array of time slots or object with timeSlots and vet for no_preference
 */
export async function fetchAvailableTimeSlots(clinicId, providerId, date, animals = []) {
  try {
    if (!currentClinicId) {
      throw new Error('Clinic ID not set. Call setClinicId() first.');
    }
    if (!clinicId) {
      throw new Error('Clinic ID is required');
    }
    // Date is required for specific vets, but optional for no_preference (to get vet assignment only)
    if (!date && providerId !== 'no_preference') {
      throw new Error('Date is required');
    }
    if (!Array.isArray(animals) || animals.length === 0) {
      throw new Error('Animals data is required and must be a non-empty array');
    }

    const url = `${API_BASE_URL}/${currentClinicId}/scheduler/available-slots/${currentClinicId}`;

    // Prepare request body according to new API format
    const requestBody = {
      clinicId: clinicId,
      vetId: providerId === 'no_preference' ? 'no_preference' : providerId,
      ...(date && { date: date }),
      days: 1,
      animals: animals.map(animal => ({
        name: animal.animalName || '',
        reason: animal.reason || '',
        species: animal.species || '',
        breed: animal.breed || ''
      }))
    };

    const headers = await getHeaders(true);
    const response = await fetchWithTokenRetry(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Backend returns the human-readable reason in `error`; fall back to `message`.
      throw new Error(errorData.error || errorData.message || 'Failed to fetch time slots');
    }

    const data = await response.json();

    // Updated response structure: { success, clinic, vet, dateRange, animals, availableSlots, totalSlots }
    if (data.success && data.availableSlots) {
      // Convert slots to 12-hour format strings
      const formattedSlots = data.availableSlots.map(slot => {
        return convertTo12Hour(slot.startTime);
      });

      // If "no preference" was selected, also return a time→vet map. The backend
      // dedupes to one vet per time slot and tags each slot with vetId/vetName, so
      // the booking step can submit a concrete vet instead of 'no_preference'.
      if (providerId === 'no_preference') {
        const slotVetByTime = {};
        data.availableSlots.forEach((slot) => {
          const t = convertTo12Hour(slot.startTime);
          slotVetByTime[t] = slot.vetId ? { id: slot.vetId, name: slot.vetName } : null;
        });
        return {
          timeSlots: formattedSlots,
          slotVetByTime,
          vet: data.vet
        };
      }

      // For specific vets, return just the formatted slots
      return formattedSlots;
    }

    // Return appropriate format for no preference vs specific vet
    return providerId === 'no_preference' ? { timeSlots: [], vet: null } : [];
  } catch (error) {
    console.error('Error fetching time slots:', error);
    throw error;
  }
}

/**
 * Fetch available breeds for dogs and cats
 * @returns {Promise<Object>} Object containing cats and dogs breeds arrays
 */
export async function fetchBreeds() {
  try {
    if (!currentClinicId) {
      throw new Error('Clinic ID not set. Call setClinicId() first.');
    }

    const url = `${API_BASE_URL}/${currentClinicId}/scheduler/breeds`;
    const headers = await getHeaders();

    const response = await fetchWithTokenRetry(url, {
      headers: headers,
    });

    if (!response.ok) throw new Error('Failed to fetch breeds');
    const data = await response.json();

    // Expected response structure: { success, breeds: { cats: [], dogs: [] }, metadata }
    if (data.success && data.breeds) {
      return data.breeds;
    }

    return { cats: [], dogs: [] }; // Return empty arrays if no breeds available
  } catch (error) {
    console.error('Error fetching breeds:', error);
    throw error;
  }
}

/**
 * Submit/create a new appointment
 * @param {Object} appointmentData - The appointment details from the form
 * @returns {Promise<Object>} Created appointment object
 */
export async function createAppointment(appointmentData) {
  try {
    if (!currentClinicId) {
      throw new Error('Clinic ID not set. Call setClinicId() first.');
    }

    // Convert 12-hour time to 24-hour format
    const startTime24 = convertTo24Hour(appointmentData.time);
    const endTime24 = calculateEndTime(startTime24, API_CONFIG.DEFAULT_APPOINTMENT_DURATION);
    
    // Check if this is a new client
    const isNewClient = appointmentData.returningClient === 'new';
    
    // Build the API payload according to the new structure
    const payload = {
      widgetKey: currentClinicId,
      clinicId: appointmentData.clinicId,
      vetId: appointmentData.assignedVet?.id || appointmentData.vetId || appointmentData.providerId, // Use assigned vet ID for no preference, fallback to vetId/providerId
      date: appointmentData.date,
      startTime: startTime24,
      endTime: endTime24,
      title: appointmentData.title || 'Appointment',
      newClient: isNewClient,
      numberOfPets: appointmentData.numberOfPets,
      pets: appointmentData.pets || [], // Array of pet objects with animalName, species, breed
      roomId: 'room1', // Hardcoded for now
      sendReminder: true,
      // Preferences and consent
      marketingOptIn: appointmentData.preferences?.marketingOptIn || false,
    };
    
    // Always include client info (the backend can decide whether to create a new client or link to existing)
    payload.client = {
      email: appointmentData.email,
      firstName: appointmentData.firstName,
      lastName: appointmentData.lastName,
      mobile: appointmentData.mobile,
      address: appointmentData.address,
      eirCode: appointmentData.eirCode,
    };
    
  

    const headers = await getHeaders(true);
    const wafIntegration = window?.AwsWafIntegration;
    const wafFetch = wafIntegration?.fetch?.bind(wafIntegration) ?? fetch;
    const response = await fetchWithTokenRetry(
      `${API_BASE_URL}/${currentClinicId}/scheduler/appointments`,
      { method: 'POST', headers: headers, body: JSON.stringify(payload) },
      wafFetch,
    );


    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Sanitize error data for logging - remove any potential PII
      const sanitizedError = {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        code: errorData.code,
        message: errorData.message,
        // Avoid logging full errorData which may contain sensitive information
      };
      console.error('API error response:', sanitizedError);
      const userMessage =
        (typeof errorData.message === 'string' && errorData.message.trim()) ||
        (typeof errorData.error === 'string' && errorData.error.trim()) ||
        'Failed to create appointment';
      throw new Error(userMessage);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating appointment:', error);
    throw error;
  }
}

